import { BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Robot, Warning } from "@phosphor-icons/react";
import { Avatar } from "@radix-ui/themes";
import cx from "classnames";

import {
  AvatarWithInitials,
  AVATAR_CLASS_NORMAL,
  AVATAR_SIZE_NORMAL,
} from "../../components/AvatarWithInitials";
import { ReactComponent as BrandRobot } from "../../icons/BrandRobot.svg";
import { ReactComponent as CopyRobot } from "../../icons/CopyRobot.svg";
import { ReactComponent as LegalRobot } from "../../icons/LegalRobot.svg";

export function renderPersonaAvatar(persona: string, block = false) {
  switch (persona.toLowerCase()) {
    case "legal":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="legal"
          radius="full"
          size="1"
          variant="solid"
          fallback={<LegalRobot />}
          className={cx(
            `${AVATAR_CLASS_NORMAL} [&>.rt-AvatarFallback]:bg-teal-teal7 [&>.rt-AvatarFallback]:text-base-black`,
            { block },
          )}
        />
      );
    case "copy":
    case "text":
    case "typography":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="copy"
          radius="full"
          size="1"
          variant="solid"
          fallback={<CopyRobot />}
          className={cx(
            `${AVATAR_CLASS_NORMAL} [&>.rt-AvatarFallback]:bg-puntt-accent-7 [&>.rt-AvatarFallback]:text-base-black`,
            { block },
          )}
        />
      );
    case "brand":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="brand"
          radius="full"
          color="amber"
          size="1"
          variant="solid"
          fallback={<BrandRobot />}
          className={cx(AVATAR_CLASS_NORMAL, { block })}
        />
      );
    default:
      return (
        <Avatar
          data-testid="ai-avatar"
          key="default"
          radius="full"
          color="amber"
          size="1"
          variant="solid"
          fallback={<Robot />}
          className={cx(AVATAR_CLASS_NORMAL, { block })}
        />
      );
  }
}

export function renderCommentAvatar(
  { isAI, aiPersona, createdBy, rule }: BaseTicketComment,
  inline = true,
) {
  if (isAI) {
    const persona = aiPersona?.avatar ? aiPersona.avatar : (rule ?? "");
    return renderPersonaAvatar(persona, !inline);
  }

  if (typeof createdBy === "string") return null;

  return (
    <AvatarWithInitials
      data-testid="user-avatar"
      name={createdBy?.name}
      avatar={createdBy?.avatar}
      size={AVATAR_SIZE_NORMAL}
      className={cx({ flex: !inline })}
    />
  );
}

/**
 * Avatar for artwork review comments.
 * Required comments: Prohibit icon on red background.
 * Non-required comments: Warning icon on gold background.
 *
 * Default sizing is AVATAR_CLASS_NORMAL (size-7 = 28px) matching the standard
 * normal avatar size. Callers that need a larger avatar (e.g. the comment-card
 * header spanning two text lines) pass explicit sizeClass + iconSize.
 */
export function renderReviewCommentAvatar(
  comment: Pick<BaseTicketComment, "isRequired">,
  // `sizeClass`/`iconSize` let a caller match the avatar to its surrounding
  // text. The comment-card header sizes it to `size-9` (36px) so the circle
  // spans the name line + the timestamp line; list/inline callers use the
  // default AVATAR_CLASS_NORMAL = size-7 (28px).
  { sizeClass = AVATAR_CLASS_NORMAL, iconSize = 16 }: AiAvatarSizing = {},
) {
  if (comment.isRequired) {
    return (
      <div
        className={cx(
          "flex shrink-0 items-center justify-center rounded-full bg-puntt-red-9",
          sizeClass,
        )}
      >
        <Prohibit size={iconSize} weight="fill" className="text-base-white" />
      </div>
    );
  }
  return (
    <div
      className={cx(
        "flex shrink-0 items-center justify-center rounded-full bg-puntt-gold-10",
        sizeClass,
      )}
    >
      <Warning size={iconSize} weight="fill" className="text-base-white" />
    </div>
  );
}

interface AiAvatarSizing {
  /** Tailwind `size-*` utility for the circle (width === height). */
  sizeClass?: string;
  /** Phosphor icon pixel size centered in the circle. */
  iconSize?: number;
}

export function mapRuleToPersona(rule?: string) {
  rule = rule?.toLowerCase();
  if (rule === "text" || rule === "typography") return "copy";
  if (rule === "brand" || rule === "legal") return rule;
  return "";
}
